import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import type { DeepKeys } from "@tanstack/table-core";
import { useMemo } from "react";

import { useChoiceQuestionComponents, choiceRulesValid } from "./choice/choice";
import { NumberValidationRules, numberRulesValid } from "./number";
import { YesNoValidationRules, yesNoRulesValid } from "./yesNo";
import { PostPublishQuestionEditWarnings } from "../post-publish-edit-warnings";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useContainsClosingPhrase } from "@/hooks/validation";
import {
  type ReviewPageBasicQuestion,
  type ChoiceBasicQuestionQuestionType,
  type QuestionStateSetter,
  QuestionType,
  type ReviewPageDraftQuestion,
} from "@/types/screener-builder";
import {
  formatClosingPhraseError,
  isChoiceQuestion,
  isQualifyingEnabledQuestion,
  POST_PUBLISH_EDIT_WARNINGS,
  questionTypeTypeGuard,
} from "@/utils/questions";
import { isEmptyAfterTrim } from "@/utils/string";

interface BasicQuestionFormProps {
  handleClose: () => void;
  isNewQuestion: boolean;
  isPublishedScreener: boolean;
  onSubmit: (question: ReviewPageDraftQuestion) => void;
  originalQuestion?: ReviewPageBasicQuestion;
  question: ReviewPageDraftQuestion;
  questionInputTestId?: string;
  setQuestion: QuestionStateSetter;
  submitButtonTestId?: string;
}

type EditWarningMap = [DeepKeys<ReviewPageBasicQuestion>, string][];

export const BasicQuestionForm = ({
  handleClose,
  isNewQuestion,
  isPublishedScreener,
  onSubmit,
  originalQuestion,
  question,
  questionInputTestId,
  setQuestion,
  submitButtonTestId,
}: BasicQuestionFormProps) => {
  const track = useTrackAnalytics();

  // Safe to cast, as this hook returns no-ops for non-choice questions but must
  // be called to satisfy rules of hooks
  const choiceComponents = useChoiceQuestionComponents(
    question,
    setQuestion as QuestionStateSetter<ChoiceBasicQuestionQuestionType>,
  );

  const hasValidConditions = useMemo(() => {
    // Choice questions need valid options regardless of qualifying status
    if (isChoiceQuestion(question)) {
      return choiceRulesValid(question);
    }

    if (questionTypeTypeGuard(QuestionType.YesNo, question)) {
      return yesNoRulesValid(question);
    }

    if (questionTypeTypeGuard(QuestionType.Number, question)) {
      return numberRulesValid(question);
    }

    // Non-qualifying questions (like ShortAnswer) are always valid
    return true;
  }, [question]);

  const { isValid: questionTextValid, errorMessage: questionTextError } =
    useContainsClosingPhrase(question.questionText || "", {
      formatFn: formatClosingPhraseError,
    });

  const editWarningMap = useMemo(() => {
    const commonWarnings: EditWarningMap = [
      ["questionText", POST_PUBLISH_EDIT_WARNINGS.answerMismatch],
    ];
    const qualifyingWarnings: EditWarningMap = [
      ["isQualifying", POST_PUBLISH_EDIT_WARNINGS.qualifyingRescoring],
      ["validationRules", POST_PUBLISH_EDIT_WARNINGS.qualifyingRescoring],
    ];
    const questionTypeWarnings: EditWarningMap = [
      // questionType is only edited for Multiple <-> Single Choice
      ["questionType", POST_PUBLISH_EDIT_WARNINGS.qualifyingRescoring],
    ];

    // Because validationRules are tangled up with options (regardless of
    // qualifying state) we need to handle two special cases around choice
    // questions.
    //   1. If the original question was single choice and qualifying, we need
    //      to warn the user that changing question type will adjust qualifying
    //   2. Other multiple choice questions exclude validationRules to prevent
    //      incorrect warnings
    if (
      question.questionType === QuestionType.MultipleChoice &&
      originalQuestion?.questionType === QuestionType.SingleChoice &&
      originalQuestion.isQualifying
    ) {
      return new Map(commonWarnings.concat(questionTypeWarnings));
    }

    return new Map(
      question.questionType === QuestionType.MultipleChoice
        ? commonWarnings
        : commonWarnings.concat(qualifyingWarnings),
    );
  }, [originalQuestion, question]);

  const isFormValid = useMemo(() => {
    return (
      !isEmptyAfterTrim(question.internalLabel) &&
      !isEmptyAfterTrim(question.questionText) &&
      hasValidConditions &&
      questionTextValid
    );
  }, [hasValidConditions, question, questionTextValid]);

  const qualifyingConditionsSection = useMemo(() => {
    if (!isQualifyingEnabledQuestion(question)) {
      return null;
    }

    if (isChoiceQuestion(question)) {
      return choiceComponents?.renderValidationRules();
    }

    if (questionTypeTypeGuard(QuestionType.Number, question)) {
      return (
        <NumberValidationRules
          question={question}
          // Safe to cast, both here and for YesNo, because the type of the
          // state setter is tied to the type of the question, which is checked
          // in the type guard
          setQuestion={setQuestion as QuestionStateSetter<QuestionType.Number>}
        />
      );
    }

    if (questionTypeTypeGuard(QuestionType.YesNo, question)) {
      return (
        <YesNoValidationRules
          question={question}
          setQuestion={setQuestion as QuestionStateSetter<QuestionType.YesNo>}
        />
      );
    }
  }, [question, setQuestion, choiceComponents]);

  return (
    <Box>
      <Stack gap="md">
        <TextInput
          label={
            <Flex align="center" gap="xs">
              <Text size="sm" fw={500}>
                Internal label <span className="text-red-500">*</span>
              </Text>
              <Tooltip
                className="cursor-pointer"
                label="A short name used to refer to a question in the Talent Hub and interview PDF (candidates won't see this)"
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="text-uw-purple-3"
                />
              </Tooltip>
            </Flex>
          }
          onBlur={(e) =>
            setQuestion({ ...question, internalLabel: e.target.value.trim() })
          }
          onChange={(e) =>
            setQuestion({ ...question, internalLabel: e.target.value })
          }
          placeholder="Enter internal label"
          required
          value={question.internalLabel || ""}
          withAsterisk={false}
        />
        <TextInput
          data-testid={questionInputTestId}
          error={questionTextError}
          label="Question"
          onBlur={(e) =>
            setQuestion({ ...question, questionText: e.target.value.trim() })
          }
          onChange={(e) =>
            setQuestion({ ...question, questionText: e.target.value })
          }
          required
          value={question.questionText}
        />

        {isChoiceQuestion(question) && choiceComponents?.renderOptions()}

        {isQualifyingEnabledQuestion(question) && (
          <>
            <hr />
            <Stack gap="sm">
              <Flex align="center" gap="xs">
                <Text size="sm" fw={500}>
                  Qualifying conditions
                </Text>
                <Tooltip
                  className="cursor-pointer"
                  label="The minimum conditions a candidate must meet to be eligible for this role; candidates who meet all qualifying conditions receive a 100% qualifying score"
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="text-uw-purple-3"
                  />
                </Tooltip>
              </Flex>
              {qualifyingConditionsSection}
            </Stack>
          </>
        )}
        <hr />
        {isChoiceQuestion(question) && (
          <Checkbox
            label="Allow multiple selections"
            checked={question.questionType === QuestionType.MultipleChoice}
            onChange={(e) => {
              const isMultiple = e.currentTarget.checked;
              const questionType = isMultiple
                ? QuestionType.MultipleChoice
                : QuestionType.SingleChoice;

              setQuestion({ ...question, questionType });
            }}
          />
        )}
        <Checkbox
          label="Show in key answers summary"
          checked={question.showInSummary}
          onChange={(e) => {
            track("Show in key answers summary Toggle Clicked", {
              question_id: question.internalLabel,
              show_in_summary: e.currentTarget.checked,
            });
            setQuestion({
              ...question,
              showInSummary: e.currentTarget.checked,
            });
          }}
        />
        <hr />
        <PostPublishQuestionEditWarnings
          enabled={!isNewQuestion && isPublishedScreener}
          value={question}
          warningMap={editWarningMap}
        />
        <Group justify="flex-start">
          <Button
            data-testid={submitButtonTestId}
            disabled={!isFormValid}
            onClick={() => onSubmit(question)}
          >
            {isNewQuestion ? "Add" : "Update"} Question
          </Button>
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};
